import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import moment from 'moment';
import find from 'ramda/src/find';
import isNil from 'ramda/src/isNil';
import propEq from 'ramda/src/propEq';
import values from 'ramda/src/values';
import Avatar from '@components/@core/Avatar';
import CheckBox from '@components/@core/CheckBox';
import Rating from '@components/@core/Rating';
import EditDropdown from '@components/EditDropdown';
import AppActions from '~/actions/app';
import ManageActions from '~/actions/manage';
import { ScorecardTaskType } from '~/services/config';
import { Unknown_User } from '~/services/constants';

const calcInput = (item, dateStart, dateEnd, type = 'status') => {
    if (item.scorecards && item.scorecards.length > 0) {
        const scorecard = item.scorecards[0];
        const quotas = scorecard.quotas;
        if (!quotas?.length) return type === 'status' ? 'No Scorecard' : 'NA';
        let sum = 0;
        quotas.forEach((_quota) => {
            const actuals = _quota.actuals.filter((actual) => moment(actual.actual_at).isBetween(dateStart, dateEnd, 'day'));
            if (actuals.length > 0) sum += 1;
        });

        if (type === 'status') {
            return item.performance?.status == 2 ? 'Completed' : 'Incomplete';
        } else {
            const percent = ((100 * sum) / quotas.length).toFixed(2).toString();
            return item.performance?.status == 2 ? '100%' : `${Number(percent)}%`;
        }
    } else {
        if (item.performance?.status == 2) return type === 'status' ? 'Completed' : '100%';
        else return type === 'status' ? 'No Scorecard' : 'NA';
    }
};

const ScorecardItem = ({ className, task, userId }) => {
    const dispatch = useDispatch();
    const users = useSelector((state) => state.app.users);
    const role = useSelector((state) => state.app.app_role_id);
    const [checked, setChecked] = useState(!!task.completed_at);
    const [dateStart, setDateStart] = useState(moment(task.data?.year_month).startOf('month').format('YYYY-MM-DD'));
    const [dateEnd, setDateEnd] = useState(moment(task.data?.year_month).endOf('month').format('YYYY-MM-DD'));

    const user = useMemo(() => {
        return find(propEq('id', task.user_id), users) || Unknown_User;
    }, [task, users]);

    const { performance, scorecards } = task?.performance;

    const Options = useMemo(() => {
        return ScorecardTaskType[role].filter((option) => option === 'Assign Scorecard');
    }, [role]);

    const handleCheck = (id) => (e) => {
        toast.warn('This task will auto complete when the Scorecard task is completed.', {
            position: toast.POSITION.TOP_RIGHT,
            pauseOnFocusLoss: false,
            hideProgressBar: true,
        });
    };

    const handleInputActuals = () => {
        const { scorecards } = task.performance;
        if (scorecards.length == 0) {
            handleAssign();
        } else {
            dispatch(
                AppActions.modalRequest({
                    type: 'Save Actuals',
                    data: {
                        before: {
                            user,
                            date: moment(dateStart).format('YYYY-MM-DD'),
                            after: {
                                type: 'INDIVIDUALPERFORMANCEREVIEW_REQUEST',
                                payload: {
                                    userId: user?.id,
                                    companyId: user?.company_id,
                                    dateStart: moment(dateStart).format('YYYY-MM-DD'),
                                    dateEnd: moment(dateEnd).format('YYYY-MM-DD'),
                                },
                            },
                        },
                    },
                })
            );
        }
    };

    const handleAssign = () => {
        dispatch(
            AppActions.modalRequest({
                type: 'Assign ToDo',
                data: {
                    before: {
                        modules: [],
                        disabled: ['habits', 'habitslist', 'quotas'],
                        assignees: [user.id],
                        after: {
                            type: 'GETTASKSFEED_REQUEST',
                            userId,
                            payload: { page: 1, perPage: 500 },
                        },
                    },
                },
            })
        );
    };

    const handleReview = () => {
        if (task.performance.scorecards.length == 0) {
            handleAssign();
        } else {
            const payload = {
                userId: task.user_id,
                companyId: task.company_id,
                dateStart,
                dateEnd,
            };
            dispatch(ManageActions.individualperformancereviewRequest(payload, `/hcm/performance-reviews/${task.user_id}?date_start=${moment(dateStart).format('YYYY-MM-DD')}`));
        }
    };

    const handleUnassignScorecard = (e) => {
        if (isNil(e.scorecards) || e.scorecards.length == 0) return;
        dispatch(
            AppActions.modalRequest({
                type: 'Confirm',
                data: {
                    before: {
                        title: 'Unassign Scorecard',
                        body: 'This will permanently delete the scorecard from your assignments.  Are you sure?',
                    },
                },
                callBack: {
                    onYes: () => {
                        dispatch(
                            ManageActions.postdeletescorecardRequest(
                                { scorecard: { id: e.scorecards[0].id } },
                                {
                                    type: 'GETTASKSFEED_REQUEST',
                                    userId,
                                    payload: { page: 1, perPage: 500 },
                                }
                            )
                        );
                    },
                },
            })
        );
    };

    const handleModal = (e) => {
        const { scorecards } = task.performance;
        switch (e) {
            case 'assign scorecard': {
                handleAssign();
                break;
            }
            case 'save actuals': {
                handleInputActuals();
                break;
            }
            case 'start review': {
                handleReview();
                break;
            }
            case 'unassign scorecard': {
                dispatch(
                    AppActions.modalRequest({
                        type: 'Quick Edit',
                        data: {
                            before: {
                                template: scorecards.length ? scorecards[0] : {},
                                userId: user.id,
                                type: 'performanceScorecards',
                                from: 'template',
                            },
                        },
                        callBack: {
                            onAssign: () => {
                                handleAssign();
                            },
                            onDelete: () => {
                                handleUnassignScorecard(task?.performance);
                            },
                        },
                    })
                );
                break;
            }
            default:
                break;
        }
    };

    return (
        <div className={classNames('scorecard-item', className)}>
            <CheckBox className="mr-2" id={task.id} size="regular" checked={checked} onChange={handleCheck(task.id)} />
            <div className="scorecard-detail" onClick={handleInputActuals}>
                <div className="d-flex ai-center mb-1">
                    <Avatar name={user.name} url={user.avatar} />
                    <div className="d-flex-2 ms-2">
                        <p className="dsl-b14 mb-0 text-400">{`${user.name} (${moment(dateStart).format('MMMM YY')})`}</p>
                        <p className="dsl-m12 mb-0 text-400">
                            {performance?.status == 2 ? values(performance.data.scorecards)[0].title : scorecards.length > 0 ? scorecards[0]?.title : 'No Scorecard Assigned'}
                        </p>
                    </div>
                    <div className={classNames('d-flex-1 text-center', performance?.status === 2 && 'd-flex-2')}>
                        {performance?.status === 2 ? (
                            <>
                                {performance?.data?.completed_average_star_rating === 'N/A' ? (
                                    <span className="dsl-b14">Na (Per Manager)</span>
                                ) : (
                                    <Rating score={performance?.data?.completed_average_star_rating} showScore={false} />
                                )}
                            </>
                        ) : (
                            <span className="dsl-b14">{calcInput(task?.performance, dateStart, dateEnd, 'actuals')}</span>
                        )}
                    </div>
                </div>
            </div>
            <EditDropdown options={Options} onChange={handleModal} />
        </div>
    );
};

export default ScorecardItem;
